import NavigationBar from "../comps/NavigationBar/NavigationBar";
import Footer from "../comps/Footer/Footer";
import ContentCard from "../comps/ContentCard/ContentCard";
import Sidebar from "../comps/Sidebar/Sidebar";

export default function Integrated() {
  const menu = [
    {
      name: "Overview",
      id: "HP-0",
    },
    {
      name: "Explore",
      id: "HP-1",
    },
    {
      name: "Design",
      id: "HP-2",
    },
    {
      name: "Implement",
      id: "HP-3",
    },
    {
      name: "Evaluate",
      id: "HP-4",
    },
  ];
  return (
    <div className="general-background">
      <NavigationBar />
      <Sidebar menu={menu} />
      <div className="main-container">
        <p className="title">Integrated HP:</p>
        <p className="subtitle" key={"HP-0"} id="HP-0">
          • Overview:
        </p>
        <ContentCard
          content={` 
            <p>
            In 2023, AI large models have had a profound impact on the world. The rise of generative models like GPT and Midjourney has further blurred the boundaries between reality and fiction. 
            </p>
            <br/>
            <p>
            As a team focused on the application of AI in synthetic biology, we have come to a deep realization that in the future world, interactions and feedback from real people and the world will be the most valuable asset. We sincerely hope that we can interact with more people, and gradually expand the impact of our projects to the whole society, not just in the campus or the laboratory. 
            </p>
            <br/>
            <p>
            Therefore, at every stage of our project, we maintain communication and collaboration with the outside world.
            </p>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure1.jpeg'
            alt='Figure 1. Flow Chart of Human Practice Framework in BioRocket'
            class='content-figure'
            style='width:70%'
            />
            </div>
            <p class='content-legend'>
            Figure 1. Flow Chart of Human Practice Framework in BioRocket
            </p>
              `}
        />
        <p className="subtitle" key={"HP-1"} id="HP-1">
          • Explore:
        </p>
        <ContentCard
          content={` 
            <p>
            During the exploration phase of our project, we identified the crucial and intriguing issue of protein delivery through literature review and brainstorming. While there are existing solutions for this problem, they come with their respective limitations. We noticed that the extracellular Contractile Injection Systems (eCISs) have been gaining more attention in recent years, but the research in this area is still in its early stages. AI may hold the potential to contribute to the expansion of eCIS toolkits.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure2.png'
            alt='Figure 2. Poster of the Latest Advances in eCIS Research'
            class='content-figure'
            style='width:70%'
            />
            </div>
            <p class='content-legend'>
            Figure 2. Poster of the Latest Advances in eCIS Research
            </p>
            <br/>
            <p>
            Together with the School of Life Sciences of Sun Yat-sen University, we held an orienteering event for the whole school, to popularize biology knowledge to college students. During the school's academic week, we summarized the latest research advancements on the eCISs and created a poster presentation. We discussed the eCISs with our teachers and students. Everyone showed a keen interest in the system and raised questions and ideas regarding its mechanical mechanisms during contraction and whether the system can be divided and expressed in different plasmids. This significantly expanded our initial perspectives. It is worth noting that our poster garnered the second prize in the college's literature reading competition, underscoring the impact of our efforts in expanding the social awareness and influence of synthetic biology.
            </p>
            <br/>
            <p>
            Our project's primary stakeholders are researchers, doctors, and pharmaceutical companies. Therefore, we have decided to initiate communication with them first to <b>learn from their experiences and gain a deeper understanding of their needs</b>.
            </p>
            <br/>
            <p>
            We have reached out to Dr. Zhongjun Li, Associate Chief Physician in the Neurosurgery Department at Sun Yat-sen Memorial Hospital (Guangzhou) and Dr. Sixue Liu, Associate Chief Physician in the Gastroenterology Department, to seek their insights on the clinical requirements and current limitations of protein delivery systems.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure3.jpeg'
            alt='Figure 3. Interact with clinical doctors'
            class='content-figure'
            style='width:70%'
            />
            </div>
            <p class='content-legend'>
            Figure 3. Interact with clinical doctors
            </p>
            <br/>
            <p>
            They shared with us the current primary clinical methods for enhancing drug delivery effectiveness involve optimizing administration techniques and modifying the microenvironment of drug absorption or action within the human body.
            </p>
            <br/>
            <p>
            Safety remains the paramount concern. eCIS, as a protein complex, may potentially pose immunogenicity issues when introduced into the human body. While most immunoreactions caused by protein drugs on the market are mild, there are isolated cases of more severe immune responses, such as respiratory depression.
            </p>
            <br/>
            <p>
            Furthermore, in clinical practice, targeted therapy drugs still lack precision. Even monoclonal antibody drugs can trigger adverse reactions in multiple parts of the body, indicating the need for improved accuracy. Therefore, the targeting potential of eCISs may offer significant prospects.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure4.jpeg'
            alt='Figure 4. Communicate with the professors'
            class='content-figure'
            style='width:70%'
            />
            </div>
            <p class='content-legend'>
            Figure 4. Communicate with the professors
            </p>
            <br/>
            <p>
            Subsequently, we engaged in discussions with several professors from the School of Life Sciences and the School of Pharmacy at Sun Yat-sen University. They expressed that there are many unknown aspects regarding the specific injection mechanism of eCISs. Additionally, questions about whether there are more eCISs in other prokaryotic organisms and if there are any patterns in their species distribution pose intriguing scientific inquiries.
            </p>
            <br/>
            <p>
            Based on the above discussion, we summarize the main issues currently associated with eCISs as a protein delivery system: lack of diversity, unclear injection mechanisms, unknown safety, and potential for optimization in terms of targeting. 
            </p>
            <br/>
            <p>
            Therefore, we believe that the project should initially focus on exploring eCISs within genomes. This forms the foundation for subsequent research and applications. Simultaneously, we should leverage the advantage of the data obtained through exploration to discover the programmability of eCISs. 
            </p>
            <br/>
            <p>
            We have not identified a feasible method for computationally predicting protein immunogenicity, which represents a limitation in our project. The primary approach to determine immunogenicity lies in the well-structured design of subsequent animal and clinical experiments (more on <a href="https://2023.igem.wiki/sysu-software/entrepreneurship">Entrepreneurship</a>).
            </p>
            `}
        />
        <p className="subtitle" key={"HP-2"} id="HP-2">
          • Design:
        </p>
        <ContentCard
          content={` 
            <p>
            AI for Synbio is not merely applying artificial intelligence algorithms to biological data, it involves selecting and enhancing appropriate AI models based on a fundamental understanding of biological systems. Therefore, during the project's design phase, <b>we engaged in discussions with both structural biology experts and artificial intelligence specialists</b>.  
            </p>
            <br/>
            <b>Discussion with structural biology experts</b>
            <br/>
            <p>
            In the early stages of project design, we encountered numerous challenges primarily due to our limited understanding of the eCISs' structure and mechanisms. During our literature review, we came across Professor Jiang Feng, who was the first to elucidate the crystal structure of PVC (one of the best-studied eCIS types) <sup>[1]</sup>. Interestingly, Professor Jiang Feng is an alumnus of Sun Yat-sen University. We proactively reached out to him, engaged in an in-person discussion, and maintained ongoing communication with him throughout the project.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure5-1.jpg'
            alt='Figure 5. Communicate with professor Jiang'
            class='content-figure'
            style='width:70%'
            />
            </div>
            <p class='content-legend'>
            Figure 5. Communicate with professor Jiang
            </p>
            <br/>
            <p>
            In May, our initial challenge was determining how to distinguish eCISs from other similar bacterial injection systems, such as T6SS, through the design of an appropriate search strategy. To address this, we consulted Professor Jiang regarding whether there were relatively specific proteins within the eCIS protein clusters. He suggested that the afp12 and afp13 proteins exhibited specificity. Consequently, we examined the positive dataset and confirmed that afp13 indeed provided effective discrimination, ultimately incorporating it into our searching criteria.  
            </p>
            <br/>
            <p>
            In July, during our team meeting, we brainstormed the idea that eCIS gene clusters might exhibit a preference for gene arrangement order, potentially serving as one of the criteria for predicting eCISs. We discussed this idea with Professor Jiang, who noted that based on the currently known eCISs, it is not possible to conclude whether eCIS gene clusters exhibit a preference for gene arrangement order.  
            </p>
            <br/>
            <p>
            In August, we encountered the challenge of determining the boundaries of eCIS gene clusters. Professor Jiang suggested that the current method for determining boundaries would involve identifying proteins that are clearly annotated as non-eCIS as the boundaries. In the subsequent phases of the project, we utilized deep learning models to predict each protein within eCISs, which significantly improved our ability to address the boundary issue.  
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure6.png'
            alt='Figure 6. Chat history with Professor Jiang'
            class='content-figure'
            style='width:70%'
            />
            </div>
            <p class='content-legend'>
            Figure 6. Chat history with Professor Jiang
            </p>
            <br/>
            <b>Discussion with artificial intelligence specialists</b>
            <br/>
            <p>
            In April, we engaged in an online discussion with Li Tianqin, the team leader of SYSU-Software in 2017. He is currently pursuing a Ph.D. degree in Natural Language Processing at Carnegie Mellon University. He believes that using pre-trained large-scale models to address protein sequence-related challenges can help overcome limitations posed by the amount of training data, potentially yielding superior results compared to traditional machine learning models. Furthermore, in fields such as image and music, generative models have become a cutting-edge research focus. If generative models could be applied to protein generation, it might lead to intriguing outcomes. We have maintained continuous communication with him throughout the subsequent stages of our project.  
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure7.jpeg'
            alt='Figure 7. Zoom meeting with Li Tianqin'
            class='content-figure'
            style='width:70%'
            />
            </div>
            <p class='content-legend'>
            Figure 7. Zoom meeting with Li Tianqin
            </p>
            `}
        />
        <p className="subtitle" key={"HP-3"} id="HP-3">
          • Implement:
        </p>
        <ContentCard
          content={` 
            <p>
            Once we have established the preliminary prediction model and generated some prediction data, a key challenge is how to conduct high-throughput validation experiments to achieve AI-assisted DBTL closed-loop.
            </p>
            <br/>
            <p>
            In order to validate the functionality of eCISs, the most direct approach is to express it in vitro. Due to the typically long size of eCIS gene clusters, approximately 20kb in length, synthesizing these genes presents challenges. Therefore, we reached out to Dr. Zongyang Yang, the founder of Emergence Synbio, a startup specializing in large fragment DNA synthesis. Dr. Yang, a former iGEMer, warmly invited us to visit and discuss matters at the company's laboratory located in Shenzhen.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure8.png'
            alt='Figure 8. The logo of Emergence Synbio'
            class='content-figure'
            style='width:40%'
            />
            </div>
            <p class='content-legend'>
            Figure 8. The logo of Emergence Synbio
            </p>
            <br/>
            <p>
            Dr. Yang is also keenly interested in the eCISs and believes it represents one of the key future applications for the synthesis of long fragments of DNA. Currently, the synthesis of eCIS gene clusters costs approximately 20,000 RMB (about 2,700 USD) and takes several months. Their long fragment synthesis technique primarily employs innovative engineered enzymes to catalyze the assembly of DNA short fragments. This has the potential to significantly reduce the cost of eCIS synthesis to around 1,000 RMB (about 150 USD).
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure9.jpg'
            alt='Figure 9. Visit Emergence Synbio's laboratory'
            class='content-figure'
            style='width:70%'
            />
            </div>
            <p class='content-legend'>
            Figure 9. Visit Emergence Synbio's laboratory
            </p>
            <br/>
            <p>
            However, Emergence Synbio is currently in the prototype development stage, and it is expected that gene synthesis will be achievable in early 2024. Therefore, it may not be feasible to collaborate on long fragment DNA synthesis within the iGEM competition cycle in 2023. We have an agreement with Dr. Yang to maintain close communication and plan to initiate relatively high-throughput eCIS protein synthesis next year.
            </p>
            <br/>
            <p>
            Simultaneously, as an entrepreneur, Dr. Yang has provided valuable insights into the commercialization direction of our project. We discussed how synthetic biology and computational biology companies can maintain their core competitiveness. He believes that the success of companies like Ginkgo Bioworks and Schrödinger underscore the importance of high-quality data as the core of synthetic biology companies. This not only relies on robust computational platforms but also necessitates high-throughput wet lab capabilities.
            </p>
            `}
        />
        <p className="subtitle" key={"HP-4"} id="HP-4">
          • Evaluate:
        </p>
        <ContentCard
          content={` 
            <p>
            Looking back in history, the emergence of new technologies has always had impacts on society that were challenging to estimate during their respective eras. These impacts could be both positive and negative. In this era, there is no doubt that AI and synthetic biology will be technologies that fundamentally transform society. While we may not be able to predict the future, extensive discussions and exchanges about new technologies are essential. 
            </p> 
            <br/>
            <p>
            In relation to our project for this year, as there are plans for commercialization, we are faced with the specific question of to what extent the algorithm and data should be open-source. Therefore, we first organized an AI for Syn-Bio webinar, primarily focusing on <b>whether artificial intelligence algorithms and data should be open-source</b> and the potential <b>challenges AI applied to synthetic biology</b> might encounter. Several teams participated in the online discussion.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure10.png'
            alt='Figure 10. AI for Syn-Bio webinar'
            class='content-figure'
            style='width:70%'
            />
            </div>
            <p class='content-legend'>
            Figure 10. AI for Syn-Bio webinar
            </p>
            <br/>
            <p>
            Regarding the first question, there was a general consensus that the AI components in their projects were often built upon open-source algorithms developed by others. Additionally, iGEM encourages the establishment of open-source communities, and therefore, it was widely believed that their algorithms and data should also be open-sourced. We largely agreed with these viewpoints but raised a new question: whether complete openness might discourage practitioner participation and hinder industrial development, and how to achieve commercialization. Based on this, we proposed that the data and algorithms generated in iGEM projects should be completely open-source. However, in the future commercialization process, consideration should be given to protecting data through patents and partial algorithm protection. This viewpoint received widespread approval.
            </p>
            <br/>
            <p>
            Additionally, we have observed that people often overlook the fact that individuals from different regions and backgrounds may have vastly different perspectives on the same issue. When it comes to the application of AI in synthetic biology, some regions might be actively promoting its development, while others could be more conservative in their approach. Therefore, cross-cultural communication is crucial.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure11.png'
            alt='Figure11. We publish posters on online platforms'
            class='content-figure'
            style='width:30%'
            />
            </div>
            <p class='content-legend'>
            Figure11. We publish posters on online platforms
            </p>
            <br/>
            <p>
            Initially, our plan was to host online discussions involving teams from all over the world, covering a diverse range of topics. We put effort into creating promotional posters and reaching out to teams through platforms like Slack. However, despite receiving interest from several teams, various factors hindered their participation in the discussions.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/integrated-figure12.png'
            alt='Figure 12. Online discussion with team Oxford'
            class='content-figure'
            style='width:70%'
            />
            </div>
            <p class='content-legend'>
            Figure 12. Online discussion with team Oxford
            </p>
            <br/>
            <p>
            Despite these challenges, we were fortunate to collaborate with the team from the University of Oxford to conduct a series of discussions focused on the application and regulation of AI in synthetic biology. This collaboration allowed us to delve into this specific area in depth and explore the complexities surrounding it. We organized the conference content into notes and aim to engage with a broader audience in the future, including students, professors, and government agencies. Our goal is to compile a white paper on the application of AI in synthetic biology, promoting its standardized use within the field.
            </p>
            <br/>
            <iframe width="100%" height="1000px" src="https://static.igem.wiki/teams/4826/wiki/investigation-and-reflections-on-policies-for-artificialintelligence-uses-in-synthetic-biology.pdf"></iframe>
            <br/>
            <p>
            Looking ahead, we remain optimistic about the upcoming Jamboree, where we anticipate engaging with teams from different parts of the world. This event provides an excellent platform for fostering meaningful exchanges of ideas and knowledge sharing. We hope to facilitate discussions that encompass a wide range of topics and create a space for teams to learn from one another.
            </p>
            `}
        />
        <p className="reference-title">• Reference:</p>
        <ContentCard
          content={`
          <p class='reference'>
          [1]Jiang F, Li N, Wang X, et al. Cryo-EM Structure and Assembly of an Extracellular Contractile Injection System. Cell. 2019;177(2):370-383.e15. doi:10.1016/j.cell.2019.02.020
          </p>
          `}
        />
      </div>
      <Footer />
    </div>
  );
}
